import * as faceapi from '@vladmandic/face-api/dist/face-api.esm.js';
import {PipelineResultImpl} from "./PipelineResult";
import {getVideoRatio, initDevice} from "../../../../utils";
import {documentValidator} from "../../../../services/documentValidator";

export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const cv = window.cv;
const MAX_FPS_DETECTION_VALUE = 500 // Optimizado: Mayor frecuencia de detección
export class Detector {
    static croppedImage = true
    static hasResponseToValidation = false
    constructor(stream, isMobile, modelPath, croppedImage, urlService) {
        this.probabilityThreshold = 50;
        this.counterSuccessfulResults = 0;
        this.MAX_FPS_DETECTION = MAX_FPS_DETECTION_VALUE;
        this.rendering = {
            now: 0,
            elapsed: 0,
            then: 0,
        };
        this.MAX_NUMBER_FACES = 1;
        this.MAX_ANGLE_TURN_HEAD = 30;
        this.MIN_FACE_SIZE_FOR_MOBILE = 224;
        this.MIN_FACE_SIZE_FOR_DESKTOP = 350;
        this.MIN_OCCUPIED_SPACE_FOR_DESKTOP = 15;
        this.MIN_OCCUPIED_SPACE_FOR_MOBILE = 15;
        this.X_OFFSET_FROM_FRAME = 15;
        this.Y_OFFSET_FROM_FRAME = 5;
        this.DEFAULT_NUMBER_SUCCESSFUL_RESULTS_FOR_AUTO_CAPTURING = 1;
        this.videoRatio = 1;
        this.showDetectedFace = false;
        this.headTurnCounter = 0;
        this.counterSuccessfulHeadTurns = 0;
        this.stream = stream;
        this.isMobile = isMobile;
        this.modelPath = modelPath;
        this.croppedImage = croppedImage
        this.urlService = urlService
    }
    setProbabilityThreshold(val) {
        this.probabilityThreshold = val;
    }
    static getInstance(stream, isMobile, modelPath, croppedImage, urlService) {
        if (!Detector.instance) {
            Detector.instance = new Detector(stream, isMobile, modelPath, croppedImage, urlService);
        }
        return Detector.instance;
    }
    updateHtmlElements(videoElement) {
        this.videoElement = videoElement;
    }
    static async initDetector(modelPath) {
        await faceapi.tf.setBackend(this.BACKEND);
        await faceapi.tf.enableProdMode();
        await faceapi.tf.ENV.set('DEBUG', false);
        await faceapi.tf.ready();
        if (!isMobile()) {
            await this.loadingModels(modelPath);
        }

    }
    static async loadingModels(modelPath) {
        if (!this.modelsLoaded()) {
            await faceapi.nets.tinyFaceDetector.load(modelPath);
            await faceapi.nets.faceLandmark68TinyNet.load(modelPath);
        }
    }
    static modelsLoaded() {
        return faceapi.nets.tinyFaceDetector.isLoaded && faceapi.nets.faceLandmark68TinyNet.isLoaded;
    }
    static async warmingUpModels() {
        // const image = new Image();
        // image.src = base64Photo;
        // await this.detectAllFaces(image);
    }

    static async resizeBase64(base64, width, height, quality, changeQuality = true) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                if (changeQuality) {
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                }

                canvas.toBlob(
                    (blob) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            resolve({
                                blob,
                                base64: reader.result
                            });
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    },
                    'image/jpeg',
                    changeQuality ? quality : 1
                );
            };
            img.onerror = reject;
            img.src = base64;
        });
    }
    static async detectAllFaces(videoSrc, croppedImage, urlService) {
        if (this.hasResponseToValidation) {
            return
        }
        this.hasResponseToValidation = true
        let result = {
            isCard: false,
            image: '',
            documentName: '',
        }
        try {
            const width = videoSrc.videoWidth;
            const height = videoSrc.videoHeight;

            // Para responder, imagen grande
            const originalCanvas = document.createElement('canvas');
            originalCanvas.width = width;
            originalCanvas.height = height;

            const contextOriginal = originalCanvas.getContext('2d');
            contextOriginal.drawImage(videoSrc, 0, 0, originalCanvas.width, originalCanvas.height);

            const originalImage = originalCanvas.toDataURL('image/jpeg');
            const blobImage = await this.resizeBase64(originalImage, width, height, 1);
            const response = await documentValidator(blobImage.blob, urlService)
            this.hasResponseToValidation = false
            result.isCard = response.data.quality
            result.image = blobImage.base64;
            if (response.data.quality) {
                result.documentName = response.data.result.documentName;
            } else {
                result.documentName = ""
            }
            return result;
        }
        catch (e) {
             console.log('error face-api', e)
            // bug in face api with canvas context 2d
        }
    }
    async startDetector() {
        if (!this.videoElement)
            throw Error("Video element not found");
        if (this.videoElement.clientWidth === 0)
            throw Error("Video element too small");
        this.rendering.then = Date.now();
        this.counterSuccessfulResults = 0;
        this.updateForIos();
        this.videoRatio = getVideoRatio(this.videoElement);
        if (!Detector.modelsLoaded()) {
            await Detector.initDetector(this.modelPath);
        }
        this.requestAnimationFrame = requestAnimationFrame(() => this.detection());
    }
    stopDetector() {
        if (this.requestAnimationFrame) {
            // @ts-ignore
            cancelAnimationFrame(this.requestAnimationFrame); // TODO
            this.requestAnimationFrame = undefined;
            if (this.innerCanvas)
                this.innerCanvas.remove();
            this.innerCanvas = undefined;
        }
    }
    updateForIos() {
        const device = initDevice();
        if (device.isIos) {
            this.DEFAULT_NUMBER_SUCCESSFUL_RESULTS_FOR_AUTO_CAPTURING = 1;
            this.MAX_FPS_DETECTION = MAX_FPS_DETECTION_VALUE; // Optimizado: Mantener misma frecuencia en iOS
            Detector.isIos = true;
        }
    }
    async detection() {
        this.requestAnimationFrame = requestAnimationFrame(() => this.detection());
        this.rendering.now = Date.now();

        this.rendering.elapsed = this.rendering.now - this.rendering.then;
        if (this.rendering.elapsed > this.MAX_FPS_DETECTION) {
            this.rendering.then = this.rendering.now - (this.rendering.elapsed % this.MAX_FPS_DETECTION);
            const detectionResult = await Detector.detectAllFaces(this.videoElement, this.croppedImage, this.urlService);
             
            /*if (this.showDetectedFace || Detector.debug)
                this.drawFaces(detectionResult.faces);*/
            // const faceErrors = await this.checkDetectedFaceOnError(detectionResult.faces);
            if (detectionResult?.isCard) {
                this.counterSuccessfulResults++;
                this.autoCapturing(detectionResult);
            }
            else {
                this.counterSuccessfulResults = 0;
                this.stream.returnErrors(false);
            }
        }
    }
    async checkDetectedFaceOnError(detectedFaces) {
        const detectionFacesForSize = faceapi.resizeResults(detectedFaces, {
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight,
        });
        const pipelineResults = new PipelineResultImpl();
        const faceDetection = this.checkNumberFaces(detectionFacesForSize);
        pipelineResults["FACE_NOT_FOUND"] = !faceDetection && detectionFacesForSize.length === 0;
        pipelineResults["TOO_MANY_FACES"] = !faceDetection && detectionFacesForSize.length > this.MAX_NUMBER_FACES;
        if (faceDetection) {
            if (cv)
                // pipelineResults["FACE_ANGLE_TOO_LARGE"] = !this.checkHeadRotation(detectionFacesForSize);
            pipelineResults["PROBABILITY_TOO_SMALL"] = !this.checkProbability(detectionFacesForSize);
            pipelineResults["FACE_TOO_SMALL"] = !this.checkFaceSize(detectionFacesForSize);
            pipelineResults["FACE_CLOSE_TO_BORDER"] = !this.checkFaceIndent(detectionFacesForSize);
        }
        return pipelineResults;
    }
    autoCapturing(result) {
        if (Detector.debug)
            console.info(this.counterSuccessfulResults);
        if (this.counterSuccessfulResults >= this.DEFAULT_NUMBER_SUCCESSFUL_RESULTS_FOR_AUTO_CAPTURING) {
            this.stream.autoCapturing(result);
            this.counterSuccessfulResults = 0;
        }
    }
    // @ts-ignore TODO
    checkNumberFaces(detectionResult) {
        return detectionResult.length === this.MAX_NUMBER_FACES;
    }
    // @ts-ignore TODO
    checkProbability(detectionResult) {
        const score = detectionResult[0].detection.score;
        return score > +this.probabilityThreshold / 100;
    }
    // @ts-ignore TODO
    checkFaceSize(detectionResult) {
        const faceBox = detectionResult[0].detection.box;
        const { width, height, area } = faceBox;
        const { imageHeight, imageWidth } = detectionResult[0].detection;
        const occupiedSize = 100 / ((imageHeight * imageWidth) / area);
        const minSide = Math.min(width / this.videoRatio, height / this.videoRatio);
        return (minSide > (this.isMobile ? this.MIN_FACE_SIZE_FOR_MOBILE : this.MIN_FACE_SIZE_FOR_DESKTOP) &&
            occupiedSize > (this.isMobile ? this.MIN_OCCUPIED_SPACE_FOR_MOBILE : this.MIN_OCCUPIED_SPACE_FOR_DESKTOP));
    }
    // @ts-ignore TODO
    checkFaceIndent(detectionResult) {
        const faceBox = detectionResult[0].detection.box;
        const { imageHeight, imageWidth } = detectionResult[0].detection;
        const { top, left, bottom, right } = faceBox;
        const topIndent = top;
        const leftIndent = left;
        const rightIndent = imageWidth - right;
        const bottomIndent = imageHeight - bottom;
        return !(topIndent < this.Y_OFFSET_FROM_FRAME ||
            leftIndent < this.X_OFFSET_FROM_FRAME ||
            rightIndent < this.X_OFFSET_FROM_FRAME ||
            bottomIndent < this.Y_OFFSET_FROM_FRAME);
    }
    // @ts-ignore TODO
    checkHeadRotation(detectionResult) {
        /*const temporaryCanvas = document.createElement("canvas");
        const dims = faceapi.matchDimensions(temporaryCanvas, this.videoElement, true);
        temporaryCanvas.remove();
        const headPosition = new HeadPosition(detectionResult[0].landmarks.positions, dims);
        let angles = [0, 0, 0];
        try {
            angles = headPosition.estimateHeadPose();
        }
        catch (e) {
            // some error with open cv
        }
        if (angles[2] > this.MAX_ANGLE_TURN_HEAD || angles[2] * -1 > this.MAX_ANGLE_TURN_HEAD) {
            this.headTurnCounter++;
        }
        else {
            this.counterSuccessfulHeadTurns++;
            if (this.counterSuccessfulHeadTurns >= 5) {
                this.headTurnCounter = 0;
                this.counterSuccessfulHeadTurns = 0;
            }
        }
        return this.headTurnCounter <= 1;*/
    }
    drawFaces(data) {
        console.log(data);
        if (!this.innerCanvas) {
            this.createCanvasForDrawingMask(this.videoElement);
        }
        const canvas = this.innerCanvas;
        if (!canvas)
            return console.error("no canvas for drawing");
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;

    }
    createCanvasForDrawingMask(video) {
        this.innerCanvas = document.createElement("canvas");
        this.innerCanvas.className = "canvas-on-video";
        this.innerCanvas.width = video.videoWidth;
        this.innerCanvas.height = video.videoHeight;
        this.videoElement.after(this.innerCanvas);
        if (this.innerCanvas.offsetWidth) {
            this.innerCanvas.setAttribute("style", `margin-left:${-(this.innerCanvas.offsetWidth / 2)}px; margin-top: ${-(this.innerCanvas.offsetHeight / 2)}px;`);
        }
    }
}
Detector.tinyFaceDetectorOptions = new faceapi.TinyFaceDetectorOptions({ inputSize: 192 });
Detector.BACKEND = 'webgl';
Detector.debug = false;
